import React, { useContext, useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { LanguageContext } from '../hooks/useLanguage';

gsap.registerPlugin(ScrollTrigger);

interface Step {
  number: string;
  title: { es: string; en: string; fr: string };
  body: { es: string; en: string; fr: string };
}

const steps: Step[] = [
  {
    number: '01',
    title: { es: 'Consulta inicial', en: 'Initial consultation', fr: 'Consultation initiale' },
    body: {
      es: 'Reunión de toma de datos: tipo de proyecto, usos previstos, plazos y coordinación con otros gremios.',
      en: 'Data-gathering meeting: project type, intended uses, timelines and coordination with other trades.',
      fr: "Réunion de collecte de données : type de projet, usages prévus, délais et coordination avec les autres corps de métier.",
    },
  },
  {
    number: '02',
    title: { es: 'Análisis de necesidades', en: 'Requirements analysis', fr: 'Analyse des besoins' },
    body: {
      es: 'Definición del alcance: zonas, circuitos, funciones y nivel de integración. Base del diseño técnico.',
      en: 'Scope definition: zones, circuits, functions and integration level. Foundation of the technical design.',
      fr: "Définition du périmètre : zones, circuits, fonctions et niveau d'intégration. Base de la conception technique.",
    },
  },
  {
    number: '03',
    title: { es: 'Propuesta técnica', en: 'Technical proposal', fr: 'Proposition technique' },
    body: {
      es: 'Documento de alcance detallado con listado de dispositivos, topología KNX y presupuesto desglosado.',
      en: 'Detailed scope document with device list, KNX topology and itemised budget.',
      fr: "Document de périmètre détaillé avec liste d'appareils, topologie KNX et budget détaillé.",
    },
  },
  {
    number: '04',
    title: { es: 'Diseño del sistema', en: 'System design', fr: 'Conception du système' },
    body: {
      es: 'Planos de instalación, esquemas de cuadro y asignación de grupos KNX. Entregado antes de obra.',
      en: 'Installation drawings, panel schematics and KNX group assignments. Delivered before works begin.',
      fr: "Plans d'installation, schémas de tableau et assignation des groupes KNX. Livré avant le début des travaux.",
    },
  },
  {
    number: '05',
    title: { es: 'Ingeniería de cuadro', en: 'Panel engineering', fr: 'Ingénierie de tableau' },
    body: {
      es: 'Diseño y montaje del cuadro KNX con etiquetado completo, bornes numeradas y canaleta ordenada.',
      en: 'KNX panel design and assembly with full labelling, numbered terminals and organised cable management.',
      fr: "Conception et montage du tableau KNX avec étiquetage complet, bornes numérotées et câblage ordonné.",
    },
  },
  {
    number: '06',
    title: { es: 'Programación KNX', en: 'KNX programming', fr: 'Programmation KNX' },
    body: {
      es: 'Configuración completa en ETS: direcciones de grupo, escenas, horarios y lógicas. Copia de seguridad incluida.',
      en: 'Full ETS configuration: group addresses, scenes, schedules and logic. Programming backup included.',
      fr: "Configuration complète dans ETS : adresses de groupe, scènes, horaires et logiques. Sauvegarde de programmation incluse.",
    },
  },
  {
    number: '07',
    title: { es: 'Instalación', en: 'Installation', fr: 'Installation' },
    body: {
      es: 'Montaje en obra con coordinación de plazos. Cada dispositivo instalado según el plano aprobado.',
      en: 'On-site installation with schedule coordination. Every device installed per the approved drawing.',
      fr: "Montage sur site avec coordination des délais. Chaque appareil installé selon le plan approuvé.",
    },
  },
  {
    number: '08',
    title: { es: 'Puesta en marcha', en: 'Commissioning', fr: 'Mise en service' },
    body: {
      es: 'Carga de programación, ajuste de parámetros y verificación de cada función con el cliente presente.',
      en: 'Programme upload, parameter adjustment and verification of every function with the client present.',
      fr: "Chargement du programme, réglage des paramètres et vérification de chaque fonction en présence du client.",
    },
  },
  {
    number: '09',
    title: { es: 'Verificación y pruebas', en: 'Testing & verification', fr: 'Tests et vérification' },
    body: {
      es: 'Lista de comprobación circuito a circuito. Cada punto probado, registrado y firmado antes de la entrega.',
      en: 'Circuit-by-circuit checklist. Every point tested, recorded and signed off before handover.',
      fr: "Liste de contrôle circuit par circuit. Chaque point testé, enregistré et validé avant la livraison.",
    },
  },
  {
    number: '10',
    title: { es: 'Entrega y formación', en: 'Handover & training', fr: 'Livraison et formation' },
    body: {
      es: 'Paquete de entrega completo: planos as-built, copia ETS, manual de usuario y sesión de formación.',
      en: 'Complete handover pack: as-built drawings, ETS backup, user manual and training session.',
      fr: "Dossier de livraison complet : plans as-built, sauvegarde ETS, manuel utilisateur et session de formation.",
    },
  },
];

const processContent = {
  label: { es: 'Cómo trabajamos', en: 'How we work', fr: 'Notre méthode' },
  title: { es: 'Diez etapas. Cada proyecto.', en: 'Ten stages. Every project.', fr: 'Dix étapes. Chaque projet.' },
  subtitle: {
    es: 'El mismo proceso en cada encargo, sin importar el tamaño. Así garantizamos que nada queda sin documentar y nada queda sin probar.',
    en: 'The same process on every project, regardless of size. That is how we guarantee nothing is left undocumented and nothing is left untested.',
    fr: "Le même processus sur chaque projet, quelle que soit sa taille. C'est ainsi que nous garantissons que rien n'est laissé sans documentation et rien sans test.",
  },
};

const CARD_WIDTH  = 340;
const CARD_GAP    = 2;

const Process: React.FC = () => {
  const { lang } = useContext(LanguageContext);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const sectionRef  = useRef<HTMLElement>(null);
  const wrapperRef  = useRef<HTMLDivElement>(null);
  const trackRef    = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  // Drag scroll state
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  useEffect(() => {
    const track = trackRef.current;
    const progress = progressRef.current;
    if (!track || !progress) return;

    const updateProgress = () => {
      const max = track.scrollWidth - track.clientWidth;
      const pct = max > 0 ? track.scrollLeft / max : 0;
      gsap.set(progress, { scaleX: pct });
      // Only update scroll-driven active if user hasn't clicked a card
      setClickedIndex(prev => {
        if (prev === null) {
          const idx = Math.round(pct * (steps.length - 1));
          setActiveIndex(Math.min(idx, steps.length - 1));
        }
        return prev;
      });
    };

    track.addEventListener('scroll', updateProgress, { passive: true });

    // Mouse drag
    const onMouseDown = (e: MouseEvent) => {
      isDragging.current = true;
      startX.current = e.pageX - track.offsetLeft;
      scrollLeft.current = track.scrollLeft;
      track.style.cursor = 'grabbing';
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      track.scrollLeft = scrollLeft.current - (x - startX.current) * 1.2;
    };
    const onMouseUp = () => {
      isDragging.current = false;
      track.style.cursor = 'grab';
    };

    // Touch drag
    const onTouchStart = (e: TouchEvent) => {
      startX.current = e.touches[0].pageX - track.offsetLeft;
      scrollLeft.current = track.scrollLeft;
    };
    const onTouchMove = (e: TouchEvent) => {
      const x = e.touches[0].pageX - track.offsetLeft;
      track.scrollLeft = scrollLeft.current - (x - startX.current) * 1.2;
    };

    track.addEventListener('mousedown', onMouseDown);
    track.addEventListener('mousemove', onMouseMove);
    track.addEventListener('mouseup', onMouseUp);
    track.addEventListener('mouseleave', onMouseUp);
    track.addEventListener('touchstart', onTouchStart, { passive: true });
    track.addEventListener('touchmove', onTouchMove, { passive: true });

    return () => {
      track.removeEventListener('scroll', updateProgress);
      track.removeEventListener('mousedown', onMouseDown);
      track.removeEventListener('mousemove', onMouseMove);
      track.removeEventListener('mouseup', onMouseUp);
      track.removeEventListener('mouseleave', onMouseUp);
      track.removeEventListener('touchstart', onTouchStart);
      track.removeEventListener('touchmove', onTouchMove);
    };
  }, []);

  const scrollToCard = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const target = index * (CARD_WIDTH + CARD_GAP);
    track.scrollTo({ left: target, behavior: 'smooth' });
    setClickedIndex(index);
    setActiveIndex(index);
    // Resume scroll-driven mode after 1.5s
    setTimeout(() => setClickedIndex(null), 1500);
  };

  return (
    <section id="proceso" ref={sectionRef} style={{ background: '#0D0D0D' }}>

      {/* Static header — not pinned */}
      <div className="container" style={{ padding: 'clamp(60px, 8vw, 120px) clamp(24px, 7vw, 120px) 60px' }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ maxWidth: '720px' }}
        >
          <span style={{
            fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 600,
            letterSpacing: '5px', textTransform: 'uppercase', color: '#C8A96E',
            display: 'block', marginBottom: '12px',
          }}>
            {processContent.label[lang]}
          </span>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(26px, 3.5vw, 48px)',
            fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.1,
            color: '#FFFFFF', marginBottom: '24px',
          }}>
            {processContent.title[lang]}
          </h2>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 'clamp(15px, 1.2vw, 18px)',
            color: '#555555', lineHeight: 1.8,
          }}>
            {processContent.subtitle[lang]}
          </p>
        </motion.div>
      </div>

      {/* Free-scroll horizontal wrapper */}
      <div ref={wrapperRef} style={{ position: 'relative' }}>

        {/* Progress bar */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
          background: 'rgba(200,169,110,0.1)', zIndex: 10,
        }}>
          <div ref={progressRef} style={{
            height: '100%',
            background: 'linear-gradient(90deg, #C8A96E, #E8C98A)',
            transformOrigin: 'left',
            transform: 'scaleX(0)',
          }} />
        </div>

        {/* Step counter */}
        <div style={{
          position: 'absolute', top: '16px', right: '40px', zIndex: 10,
          fontFamily: 'var(--font-mono)', fontSize: '11px',
          color: '#555555', letterSpacing: '1px',
        }}>
          {String(Math.max(activeIndex + 1, 1)).padStart(2, '0')} / 10
        </div>

        {/* Horizontal scrollable track */}
        <div
          ref={trackRef}
          style={{
            display: 'flex',
            gap: `${CARD_GAP}px`,
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingLeft: 'clamp(24px, 7vw, 120px)',
            paddingRight: 'clamp(24px, 7vw, 120px)',
            paddingTop: '40px',
            paddingBottom: '48px',
            cursor: 'grab',
            userSelect: 'none',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {steps.map((step, i) => {
            const isActive  = i === activeIndex;
            const isHovered = i === hoveredIndex;
            const highlight = isActive || isHovered;
            return (
              <div
                key={step.number}
                onClick={() => scrollToCard(i)}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  width: `${CARD_WIDTH}px`,
                  flexShrink: 0,
                  background: isActive ? '#2D2D2D' : isHovered ? '#202020' : '#1A1A1A',
                  padding: '48px 40px',
                  borderTop: isActive ? '2px solid #C8A96E' : isHovered ? '2px solid rgba(200,169,110,0.4)' : '2px solid transparent',
                  transition: 'background 0.3s, border-color 0.3s',
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: '13px', fontWeight: 300,
                  color: '#C8A96E', lineHeight: 1, marginBottom: '28px',
                  letterSpacing: '1px',
                  opacity: highlight ? 1 : 0.35,
                  transition: 'opacity 0.3s',
                }}>
                  {step.number} / 10
                </div>
                <div style={{
                  fontFamily: 'var(--font-display)', fontSize: '16px', fontWeight: 600,
                  color: isActive ? '#FFFFFF' : isHovered ? '#CCCCCC' : '#444444',
                  marginBottom: '16px', transition: 'color 0.3s', lineHeight: 1.3,
                }}>
                  {step.title[lang]}
                </div>
                <div style={{
                  fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 400,
                  color: isActive ? '#888888' : isHovered ? '#555555' : '#2D2D2D',
                  lineHeight: 1.8, transition: 'color 0.3s',
                }}>
                  {step.body[lang]}
                </div>
              </div>
            );
          })}
        </div>

        {/* Scroll hint — fades out after interaction */}
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: activeIndex >= 1 ? 0 : 1 }}
          style={{
            position: 'absolute', bottom: '40px', right: '40px',
            display: 'flex', alignItems: 'center', gap: '12px',
            fontFamily: 'var(--font-body)', fontSize: '11px',
            letterSpacing: '2px', color: '#555555', textTransform: 'uppercase',
          }}
        >
          {lang === 'es' ? 'Desplaza para ver' : lang === 'en' ? 'Scroll to explore' : 'Défiler pour explorer'}
          <span style={{ fontSize: '18px' }}>→</span>
        </motion.div>
      </div>

      {/* Bottom note */}
      <div className="container" style={{ padding: '0 clamp(24px, 7vw, 120px) clamp(60px, 8vw, 120px)' }}>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{
            background: '#1A1A1A',
            padding: 'clamp(24px, 2.5vw, 40px)',
            borderLeft: '3px solid #C8A96E',
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', flexWrap: 'wrap', gap: '24px',
          }}
        >
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '15px', color: '#AAAAAA', lineHeight: 1.6 }}>
            {lang === 'es' && 'Cada proyecto incluye un paquete de entrega completo. Sin excepciones.'}
            {lang === 'en' && 'Every project includes a complete handover pack. No exceptions.'}
            {lang === 'fr' && "Chaque projet comprend un dossier de livraison complet. Sans exception."}
          </p>
          <motion.a
            href="#contact"
            whileHover={{ background: '#C8A96E', color: '#0D0D0D' }}
            style={{
              padding: '14px 32px', border: '1px solid #C8A96E',
              fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 700,
              letterSpacing: '3px', textTransform: 'uppercase', color: '#C8A96E',
              transition: 'all 0.3s', whiteSpace: 'nowrap', display: 'inline-block',
            }}
          >
            {lang === 'es' ? 'Iniciar proyecto' : lang === 'en' ? 'Start your project' : 'Démarrer votre projet'}
          </motion.a>
        </motion.div>
      </div>

    </section>
  );
};

export default Process;
